import React, {useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import PropTypes from 'prop-types';
import {yupResolver} from '@hookform/resolvers/yup';
import * as Yup from 'yup';

import {Dialog, DialogContent, Fade, Stack, Typography} from '@mui/material';
import {LoadingButton} from '@mui/lab';

import {DialogTitleWithCloseIcon} from '../../@extended/CustomDialogs';
import {FormInput, FormNumber, SimpleFormSelectWithLabel} from '../../@extended/Forms';
import {useCreateAccountMutation} from '../../../../features/coreApi/apiSlice';

const accountSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    rentLateDate: Yup.number()
        .transform((value, originalValue) => {
            return originalValue === '' ? undefined : value;
        })
        .typeError('Rent Late Date must be a number')
        .required('Rent Late Date is required')
        .min(1, 'Rent Late Date must be greater than 0'),
    status: Yup.string().required()
});

const accountStatus = [
    {id: 'Demoing', name: 'Demoing'},
    {id: 'Onboarding', name: 'Onboarding'}
];

const NewAccount = ({isOpen, setOpen}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [addAccount] = useCreateAccountMutation();

    const methods = useForm({
        defaultValues: {
            name: '',
            rentLateDate: 4,
            status: 'Demoing'
        },
        mode: 'all',
        resolver: yupResolver(accountSchema)
    });

    const {
        reset,
        getValues,
        formState: {isValid, isDirty}
    } = methods;

    const handleClose = () => {
        reset();
        setIsLoading(false);
        setOpen(false);
    };

    const onSubmit = async () => {
        try {
            setIsLoading(true);
            const data = getValues();

            const accountData = {
                name: data['name'],
                account_status__c: data['status'],
                rent_late_date__c: data['rentLateDate']
            };

            await addAccount(accountData).unwrap();
            handleClose();
        } catch (error) {
            console.error('Error adding account:', error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Dialog open={isOpen} onClose={() => handleClose()} fullWidth maxWidth={'lg'}>
            <DialogTitleWithCloseIcon onClose={() => handleClose()}>New Account</DialogTitleWithCloseIcon>
            <DialogContent dividers sx={{pt: 0, height: '30vh'}}>
                <FormProvider {...methods}>
                    <form style={{width: '100%', height: '100%'}}>
                        <Stack
                            direction="column"
                            rowGap={2}
                            width={'100%'}
                            height={'100%'}
                            sx={{p: '24px 0'}}
                            justifyContent={'space-between'}
                        >
                            <Stack direction="column" rowGap={3}>
                                <Stack direction="column" rowGap={1}>
                                    <Stack direction="row" columnGap={1}>
                                        <FormInput name="name" label="Account Name" />
                                    </Stack>
                                    <Stack direction="row" columnGap={1} width={'100%'}>
                                        <FormNumber name="rentLateDate" label="Rent Late Date" />
                                        <SimpleFormSelectWithLabel
                                            name="status"
                                            options={[{id: 'default', name: 'Select an account status'}, ...accountStatus]}
                                        />
                                    </Stack>
                                </Stack>
                            </Stack>
                            <Stack direction={'column'} rowGap={1} justifyContent={'space-between'} alignItems={'flex-end'}>
                                <Fade in={isLoading}>
                                    <Typography variant="body2" color={'text.subtitle'}>
                                        Adding a new account, it will take a few seconds...
                                    </Typography>
                                </Fade>
                                <LoadingButton
                                    variant="contained"
                                    loading={isLoading}
                                    color="secondary"
                                    onClick={onSubmit}
                                    disabled={!(isValid && isDirty)}
                                >
                                    Add Account
                                </LoadingButton>
                            </Stack>
                        </Stack>
                    </form>
                </FormProvider>
            </DialogContent>
        </Dialog>
    );
};

NewAccount.propTypes = {
    isOpen: PropTypes.bool,
    setOpen: PropTypes.func
};

export default NewAccount;
